import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ChatsCircle, Phone, ChatCircleDots, Robot, UserCircle, Headset, CircleNotch,
} from "@phosphor-icons/react";

export default function Conversations() {
  const [convos, setConvos] = useState(null);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/conversations").then((r) => {
      setConvos(r.data);
      if (r.data.length) setActive(r.data[0]);
    }).catch(() => setConvos([]));
  }, []);

  if (convos === null) {
    return <div className="p-20 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#0033FF]" /></div>;
  }

  return (
    <div className="p-6 sm:p-8">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Konversationer</h1>
        <p className="mt-1 text-zinc-500">Granska varje chatt och samtal dina agenter har hanterat.</p>
      </div>

      {convos.length === 0 ? (
        <div data-testid="conversations-empty" className="mt-10 border border-dashed border-zinc-300 rounded-xl bg-white p-16 text-center">
          <ChatsCircle size={32} weight="duotone" className="mx-auto text-zinc-300" />
          <h3 className="mt-4 font-display font-bold text-xl">Inga konversationer än</h3>
          <p className="mt-2 text-zinc-500">När kunder pratar med dina agenter visas utskrifterna här.</p>
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-200 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
              {convos.length} konversationer
            </div>
            <div className="max-h-[600px] overflow-y-auto scroll-thin divide-y divide-zinc-100">
              {convos.map((c) => {
                const last = c.messages?.[c.messages.length - 1];
                return (
                  <button key={c.id} data-testid={`conversation-${c.id}`} onClick={() => setActive(c)}
                    className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors ${active?.id === c.id ? "bg-[#0033FF]/5" : ""}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 font-semibold text-sm truncate">
                        {c.channel === "voice" ? <Phone size={14} weight="bold" className="text-[#0033FF]" /> : <ChatCircleDots size={14} weight="bold" className="text-[#0033FF]" />}
                        {c.agent_name || "Agent"}
                      </span>
                      {c.escalated && <Headset size={15} weight="bold" className="text-amber-500 shrink-0" />}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1 truncate">{last?.content || "—"}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg flex flex-col">
            {active ? (
              <>
                <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {active.channel === "voice" ? <Phone size={18} weight="bold" className="text-[#0033FF]" /> : <ChatCircleDots size={18} weight="bold" className="text-[#0033FF]" />}
                    <span className="font-display font-bold">{active.agent_name || "Agent"}</span>
                    <span className="text-xs text-zinc-400 uppercase tracking-wide">· {active.channel}</span>
                  </div>
                  {active.escalated && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                      <Headset size={14} weight="bold" /> Eskalerad
                    </span>
                  )}
                </div>
                <div data-testid="conversation-transcript" className="flex-1 p-5 space-y-3 max-h-[540px] overflow-y-auto scroll-thin bg-[#FAFAFA]">
                  {active.messages?.map((m, i) => (
                    <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      {m.role === "assistant" && <div className="h-7 w-7 rounded-full bg-[#0033FF]/10 flex items-center justify-center shrink-0"><Robot size={15} weight="fill" className="text-[#0033FF]" /></div>}
                      <div className={`max-w-[75%] text-sm px-3.5 py-2.5 rounded-2xl whitespace-pre-wrap ${m.role === "user" ? "rounded-br-sm bg-[#0033FF] text-white" : "rounded-bl-sm bg-white border border-zinc-200"}`}>
                        {m.content}
                      </div>
                      {m.role === "user" && <div className="h-7 w-7 rounded-full bg-zinc-200 flex items-center justify-center shrink-0"><UserCircle size={16} weight="fill" className="text-zinc-500" /></div>}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-zinc-400">Välj en konversation</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
